import { AiActionPolicyV1Schema } from "./schemas/ai-action-policy";
import type { CampaignCreationCapabilities } from "./schemas/campaign-creation";
import type {
  SmartCreationDraft,
  SmartCreationDraftContentV2,
  SmartCreationStrategyLane,
} from "./schemas/smart-creation";
import type {
  SmartCreationAutomaticBidInitialization,
  SmartCreationReviewBudgetSuggestion,
  SmartCreationReviewEditChange,
  SmartCreationReviewWorkspace,
} from "./schemas/smart-creation-review";
import { applyChange } from "./smart-creation-review-workspace/apply-change";
import { preparePreciseAutomaticSelection } from "./smart-creation-review-workspace/budget";
import { campaignCopySeed } from "./smart-creation-review-workspace/campaign-copy";
import { updateDraftContentFromGraph } from "./smart-creation-review-workspace/draft-projection";
import { SmartCreationReviewError } from "./smart-creation-review-workspace/errors";
import {
  cloneWorkspace,
  finalizeWorkspace,
  summaryFor,
  withoutContentHash,
} from "./smart-creation-review-workspace/finalize";
import { generatedValue, graphFromDraftContent } from "./smart-creation-review-workspace/graph";
import type { ReviewProvenance } from "./smart-creation-review-workspace/types";
import { assertWorkspaceLimits } from "./smart-creation-review-workspace/validation";

export {
  SmartCreationReviewError,
  type SmartCreationReviewErrorCode,
} from "./smart-creation-review-workspace/errors";

const REVIEW_SCHEMA_VERSION = 2 as const;

export interface SmartCreationReviewWorkspaceInput {
  pendingStrategyLanes?: SmartCreationStrategyLane[];
  strategyPreparation?: SmartCreationReviewWorkspace["strategyPreparation"];
  strategyIntentCoverage?: SmartCreationReviewWorkspace["strategyIntentCoverage"];
  planRevision: number;
  /** Present for persisted drafts; omitted by older callers with campaign content only. */
  planId?: string;
  shopId?: string;
  budgetSuggestion?: SmartCreationReviewBudgetSuggestion;
  bidBoundsSuggestion?: SmartCreationReviewWorkspace["bidBoundsSuggestion"];
  automaticBidInitializations?: SmartCreationAutomaticBidInitialization[];
  draftContent: SmartCreationDraftContentV2;
  capabilities: CampaignCreationCapabilities;
  planName?: string;
  portfolioId?: string | null;
  endDate?: string | null;
  provenance?: ReviewProvenance;
  now: Date;
}

export function createSmartCreationReviewWorkspace(
  input: SmartCreationReviewWorkspaceInput,
): SmartCreationReviewWorkspace {
  const { draftContent, capabilities } = input;
  const graph = graphFromDraftContent(draftContent, capabilities, input.provenance);
  // The compiler budgets selected Campaigns; unchecked Precise candidates keep
  // legal placeholders without adding them to the Plan's selected budget.
  for (const campaign of graph.campaigns) {
    const targets = graph.targets.filter((target) =>
      campaign.adGroupLineageKeys.includes(target.adGroupLineageKey),
    );
    if (targets.length > 0 && targets.every((target) => target.excluded.effective))
      campaign.excluded = generatedValue(true);
  }
  const effectiveDraftContent = updateDraftContentFromGraph(draftContent, graph);
  for (const record of input.automaticBidInitializations ?? []) {
    const group = graph.adGroups.find((item) => item.ref.itemKey === record.adGroupKey);
    if (!group) continue;
    for (const target of graph.targets) {
      if (target.type !== "automatic" || target.adGroupLineageKey !== group.ref.lineageKey)
        continue;
      const recommendation = record.items.find(
        (item) => item.expressionType === target.expressionType,
      );
      if (!recommendation) continue;
      target.bid = { effective: recommendation.initialBid };
    }
  }
  const projectionKeys = graph.campaigns.map((campaign) => campaign.ref.lineageKey);
  return finalizeWorkspace({
    schemaVersion: REVIEW_SCHEMA_VERSION,
    workspaceRevision: 1,
    basedOn: {
      planRevision: input.planRevision,
      previewGeneratorVersion: draftContent.previewGeneratorVersion,
      identityVersion: draftContent.identityVersion,
      capabilityFingerprint: draftContent.capabilityFingerprint,
    },
    status: "ready",
    ...(input.pendingStrategyLanes ? { pendingStrategyLanes: input.pendingStrategyLanes } : {}),
    ...(input.strategyPreparation ? { strategyPreparation: input.strategyPreparation } : {}),
    ...(input.strategyIntentCoverage
      ? { strategyIntentCoverage: input.strategyIntentCoverage }
      : {}),
    suggestedDraft: draftContent,
    draftContent: effectiveDraftContent,
    ...(input.budgetSuggestion ? { budgetSuggestion: input.budgetSuggestion } : {}),
    ...(input.bidBoundsSuggestion ? { bidBoundsSuggestion: input.bidBoundsSuggestion } : {}),
    ...(input.automaticBidInitializations
      ? { automaticBidInitializations: input.automaticBidInitializations }
      : {}),
    ...(input.planId && input.shopId
      ? {
          aiActionPolicy: AiActionPolicyV1Schema.parse({
            version: 1,
            revision: 1,
            shopId: input.shopId,
            source: "smart_creation",
            planId: input.planId,
            ...(input.bidBoundsSuggestion
              ? {
                  bidBounds: {
                    min: input.bidBoundsSuggestion.min,
                    max: input.bidBoundsSuggestion.max,
                  },
                }
              : {}),
          }),
        }
      : {}),
    planSettings: {
      ref: { kind: "plan", itemKey: null, lineageKey: "plan" },
      name: generatedValue(
        input.planName ?? `AI Campaign Plan ${draftContent.campaigns[0]?.startDate}`,
      ),
      portfolioId: generatedValue<string | null>(input.portfolioId ?? null),
      startDate: draftContent.campaigns[0]?.startDate ?? "1970-01-01",
      endDate:
        input.endDate !== undefined ? input.endDate : (draftContent.campaigns[0]?.endDate ?? null),
    },
    graph,
    projections: {
      structure: { campaignLineageKeys: projectionKeys },
      table: { campaignLineageKeys: projectionKeys },
    },
    diagnostics: [],
    summary: summaryFor(
      graph,
      draftContent,
      input.endDate !== undefined ? input.endDate : (draftContent.campaigns[0]?.endDate ?? null),
    ),
    safety: { campaignState: "PAUSED", aiManagement: "OFF", amazonWrites: false },
  });
}

/** Validate the editable workspace and retain blocking diagnostics. */
export function normalizeSmartCreationLocalReviewWorkspace(
  current: SmartCreationReviewWorkspace,
): SmartCreationReviewWorkspace {
  if (current.status === "confirmed") return cloneWorkspace(current);
  const workspace = cloneWorkspace(current);
  workspace.schemaVersion = REVIEW_SCHEMA_VERSION;
  if (workspace.diagnostics.some((item) => item.blocking))
    workspace.status = "preview_refresh_required";
  return finalizeWorkspace(withoutContentHash(workspace));
}

/** Local edits change effective state only; they do not become commands to replay after regeneration. */
export function applySmartCreationLocalReviewChanges(
  current: SmartCreationReviewWorkspace,
  changes: readonly SmartCreationReviewEditChange[],
  capabilities: CampaignCreationCapabilities,
  now = new Date(),
  promotionDateRange: SmartCreationDraft["promotionDateRange"] = null,
): SmartCreationReviewWorkspace {
  if (current.status === "confirmed") {
    throw new SmartCreationReviewError(
      409,
      "smart_creation.plan_immutable",
      "A confirmed Review cannot be edited",
    );
  }
  const workspace = normalizeSmartCreationLocalReviewWorkspace(current);
  const preparedSelections = preparePreciseAutomaticSelection(workspace, changes, capabilities);
  for (const change of changes) {
    const campaignSeed =
      change.operation === "add_campaign" ? campaignCopySeed(workspace, change) : undefined;
    if (!preparedSelections.has(change))
      applyChange(workspace, change, capabilities, campaignSeed, { now, promotionDateRange });
  }
  assertWorkspaceLimits(workspace, capabilities);
  workspace.workspaceRevision += 1;
  workspace.draftContent = updateDraftContentFromGraph(workspace.draftContent, workspace.graph);
  workspace.summary = summaryFor(
    workspace.graph,
    workspace.draftContent,
    workspace.planSettings.endDate,
  );
  return finalizeWorkspace(withoutContentHash(workspace));
}

export function markSmartCreationReviewRefreshPreviewRequired(
  current: SmartCreationReviewWorkspace | null,
): SmartCreationReviewWorkspace | null {
  if (!current || current.status === "confirmed") return current;
  const workspace = cloneWorkspace(current);
  workspace.status = "preview_refresh_required";
  workspace.diagnostics = [
    {
      severity: "error",
      code: "preview_refresh_required",
      blocking: true,
      entity: null,
      field: null,
      message: "Draft inputs changed. Regenerate the preview; this replaces saved Review edits.",
    },
  ];
  return finalizeWorkspace(withoutContentHash(workspace));
}

export function confirmSmartCreationReviewWorkspace(
  workspace: SmartCreationReviewWorkspace,
): SmartCreationReviewWorkspace {
  if (
    workspace.status !== "ready" ||
    workspace.diagnostics.some((item) => item.blocking) ||
    (workspace.pendingStrategyLanes?.length ?? 0) > 0
  ) {
    throw new SmartCreationReviewError(
      409,
      "smart_creation.review_not_confirmable",
      "Resolve draft review issues and refresh the preview before confirming",
    );
  }
  return finalizeWorkspace({ ...withoutContentHash(workspace), status: "confirmed" });
}
